import type { Row } from '@tanstack/react-table';
import { cn } from '@tempo-io/sui-class-utils';
import {
  Dependency,
  InfoFilled,
  KeyboardArrowDown,
  KeyboardArrowRight,
  SquareAttention,
  VisibilityOff,
} from '@tempo-io/sui-icons';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@tempo-io/sui-tooltip';
import { useTranslation } from 'react-i18next';
import { NativeItemIndicator } from '@/components/common/icons/NativeItemIndicator';
import type { DataTableItem } from '@/components/common/items-data-table/types';
import { ItemTypeIcon } from '@/components/ui/item-type/itemTypeIcon';
import type { ItemType } from '@/lib/api/types/itemType';
import { useItemPanelNavigation } from '@/lib/hooks/item-panel/useItemPanelParams';

const TOOLTIP_OFFSET_PIXELS = 32;

type MissingSchedulingInfoWarningIconProps = {
  hasTeam: boolean;
  hasEstimation: boolean;
  hasDescendantWarning: boolean;
  isExpanded: boolean;
};

const MissingSchedulingInfoWarningIcon = ({
  hasTeam,
  hasEstimation,
  hasDescendantWarning,
  isExpanded,
}: MissingSchedulingInfoWarningIconProps) => {
  const { t } = useTranslation();

  const isMissingSchedulingInfo = !hasTeam || !hasEstimation;
  const showDescendantWarning = !isMissingSchedulingInfo && hasDescendantWarning && !isExpanded;

  if (!isMissingSchedulingInfo && !showDescendantWarning) {
    return null;
  }

  const tooltipMessage = isMissingSchedulingInfo
    ? t(($) => $.adaptivePlanner.backlogTable.missingSchedulingInfo)
    : t(($) => $.adaptivePlanner.backlogTable.descendantMissingSchedulingInfo);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger>
          <SquareAttention
            className="text-text-warning"
            data-testid="missing-scheduling-info-icon"
          />
        </TooltipTrigger>
        <TooltipContent className="max-w-45">{tooltipMessage}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const MissingItemWarningIcon = ({
  isDescendantWarning,
}: {
  isDescendantWarning: boolean;
}) => {
  const { t } = useTranslation();

  const message = isDescendantWarning
    ? t(($) => $.adaptivePlanner.backlogTable.descendantMissingItem.label)
    : t(($) => $.adaptivePlanner.backlogTable.missingItem.label);

  const sync = isDescendantWarning
    ? t(($) => $.adaptivePlanner.backlogTable.descendantMissingItem.sync)
    : t(($) => $.adaptivePlanner.backlogTable.missingItem.sync);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger>
          <InfoFilled className="text-text-error" data-testid="missing-item-icon" />
        </TooltipTrigger>
        <TooltipContent className="max-w-md">
          <div className="space-y-2">
            <p className="text-pretty">{message}</p>
            <p className="text-pretty">{sync}</p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const DependencyIcon = ({ dependencyCount }: { dependencyCount?: number }) => {
  const hasDependency = dependencyCount && dependencyCount > 0;

  if (!hasDependency) {
    return <div />;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger>
          <Dependency />
        </TooltipTrigger>
        <TooltipContent>{dependencyCount} Dependencies</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const ItemNameCellContent = ({
  itemName,
  itemType,
  externalKey,
  rowDepth,
  canExpand,
  isExpanded,
  toggleExpanded,
  onItemNameClick,
  dependencyCount,
  additionalIcons,
  isRestricted,
  disableNameClick,
  tabIndex,
  className,
  style,
  ...containerProps
}: {
  itemName: string;
  itemType?: ItemType;
  externalKey?: string | null;
  rowDepth: number;
  canExpand?: boolean;
  isExpanded?: boolean;
  toggleExpanded?: () => void;
  onItemNameClick?: () => void;
  dependencyCount?: number;
  additionalIcons?: React.ReactNode;
  isRestricted?: boolean;
  disableNameClick?: boolean;
  tabIndex?: 0 | -1;
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'itemType'>) => {
  return (
    <div
      {...containerProps}
      className={cn('grid grid-cols-[minmax(1rem,100%)_max-content]', className)}
      style={{
        paddingLeft: `${rowDepth * 2}rem`,
        ...style,
      }}
    >
      <div className="flex items-center">
        <div className="flex h-5 min-w-5 max-w-5 items-center justify-center">
          {canExpand && (
            <button
              onClick={toggleExpanded}
              type="button"
              tabIndex={tabIndex}
              className="flex items-center justify-center"
            >
              {isExpanded ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
            </button>
          )}
        </div>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={disableNameClick ? undefined : onItemNameClick}
                className={cn('overflow-hidden flex items-center gap-1', {
                  'cursor-default italic text-text-neutral-secondary': isRestricted,
                  'cursor-default': disableNameClick && !isRestricted,
                  'cursor-pointer': !isRestricted && !disableNameClick,
                })}
                type="button"
                aria-disabled={isRestricted || disableNameClick}
                tabIndex={isRestricted || disableNameClick ? -1 : tabIndex}
              >
                {isRestricted ? (
                  <VisibilityOff className="flex-none" />
                ) : (
                  !!itemType && <ItemTypeIcon className="flex-none" itemType={itemType} />
                )}
                <span className="min-w-0 truncate">{itemName}</span>
                {!isRestricted && <NativeItemIndicator externalKey={externalKey} />}
              </button>
            </TooltipTrigger>
            <TooltipContent align="start" alignOffset={TOOLTIP_OFFSET_PIXELS} side="bottom">
              {!isRestricted && <strong className="mr-1">{itemType?.name}:</strong>}
              {itemName}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      <div className="flex items-center gap-1 justify-self-end">
        {additionalIcons}
        <DependencyIcon dependencyCount={dependencyCount} />
      </div>
    </div>
  );
};

export const ItemNameCell = ({
  row,
  showSchedulingWarning = false,
}: {
  row: Row<DataTableItem>;
  showSchedulingWarning?: boolean;
}) => {
  const { openItemPanel } = useItemPanelNavigation();
  const itemId = row.original.id;

  const handleClick = () => {
    openItemPanel({ itemId });
  };

  const itemName = row.original.name;
  const itemType = row.original.itemType;
  const externalKey = row.original.externalKey;
  const itemTeam = row.original.itemTeam;
  const estimation = row.original.estimation;
  const hasDescendantMissingSchedulingInfo =
    row.original.hasDescendantMissingSchedulingInfo ?? false;
  const dependencyCount = row.original.dependencyCount ?? 0;
  const rowDepth = row.depth;
  const isExpanded = row.getIsExpanded();

  const handleExpandRow = () => {
    row.toggleExpanded();
  };

  const isMissingItem = row.original.isMissingFromPortfolio ?? false;
  const hasDescendantMissingFromPortfolio = row.original.hasDescendantMissingFromPortfolio ?? false;
  const showDescendantMissingWarning =
    !isMissingItem && hasDescendantMissingFromPortfolio && !isExpanded;
  const showMissingItemIcon = isMissingItem || showDescendantMissingWarning;

  const isParentItem = row.getCanExpand();
  const canShowBothIcons = isParentItem && showMissingItemIcon && showSchedulingWarning;
  const shouldShowSchedulingWarning =
    canShowBothIcons || (!showMissingItemIcon && showSchedulingWarning);

  return (
    <ItemNameCellContent
      itemName={itemName}
      itemType={itemType}
      externalKey={externalKey}
      rowDepth={rowDepth}
      canExpand={row.getCanExpand()}
      isExpanded={isExpanded}
      toggleExpanded={handleExpandRow}
      onItemNameClick={handleClick}
      dependencyCount={dependencyCount}
      additionalIcons={
        <>
          {showMissingItemIcon && (
            <MissingItemWarningIcon isDescendantWarning={showDescendantMissingWarning} />
          )}
          {shouldShowSchedulingWarning && (
            <MissingSchedulingInfoWarningIcon
              hasTeam={!!itemTeam}
              hasEstimation={!!estimation}
              hasDescendantWarning={hasDescendantMissingSchedulingInfo}
              isExpanded={isExpanded}
            />
          )}
        </>
      }
    />
  );
};
